import type { KeyboardEvent } from 'react'
import type { Option } from '@/components/ui/multi-select/useMultiSelect'

interface KeyDownContext {
  open: boolean
  setOpen: (open: boolean) => void
  search: string
  filtered: Option[]
  activeIndex: number
  setActiveIndex: (update: number | ((index: number) => number)) => void
  value: string[]
  toggle: (optionValue: string) => void
  removeTag: (optionValue: string) => void
  close: () => void
}

/** Keyboard handler for the MultiSelect combobox input (arrows, Home/End, Enter, Escape, Backspace). */
export function useMultiSelectKeyDown(ctx: KeyDownContext) {
  const { open, setOpen, search, filtered, activeIndex, setActiveIndex, value, toggle, removeTag, close } = ctx

  // Arrow keys open the closed list, then cycle the active option (wrapping).
  const openListOrMoveActive = (e: KeyboardEvent<HTMLInputElement>, delta: number) => {
    e.preventDefault()
    if (!open) {
      setOpen(true)
      return
    }
    if (filtered.length) setActiveIndex((i) => (i + delta + filtered.length) % filtered.length)
  }

  // Home/End jump to the ends of the open list.
  const jumpActive = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (open && filtered.length) {
      e.preventDefault()
      setActiveIndex(index)
    }
  }

  const selectActive = (e: KeyboardEvent<HTMLInputElement>) => {
    if (open && activeIndex >= 0 && activeIndex < filtered.length) {
      e.preventDefault()
      toggle(filtered[activeIndex].value)
    }
  }

  const handleEscape = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open) return
    e.preventDefault()
    close()
  }

  const handleBackspace = (e: KeyboardEvent<HTMLInputElement>) => {
    if (search === '' && value.length > 0) {
      e.preventDefault()
      removeTag(value[value.length - 1])
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        return openListOrMoveActive(e, 1)
      case 'ArrowUp':
        return openListOrMoveActive(e, -1)
      case 'Home':
        return jumpActive(e, 0)
      case 'End':
        return jumpActive(e, filtered.length - 1)
      case 'Enter':
        return selectActive(e)
      case 'Escape':
        return handleEscape(e)
      case 'Backspace':
        return handleBackspace(e)
    }
  }

  return handleKeyDown
}
